import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { ProgressBarGradient } from '../../components/ProgressBarGradient';
import { customPalette } from '../../themes/customTheme';
import { apiUrl, httpClient } from '../../utils/httpUtils';
import { hasRole } from '../../utils/roles';

const defaultProgress = ({ currentValue, target }) => {
  if (target > 0 && currentValue > 0) {
    return (currentValue * 100) / target;
  } else {
    return 0;
  }
};

const ProgressBar = ({ currentValue, target, color }) => {
  let progressValue = Math.round(defaultProgress({ currentValue, target }));
  return (
    <Box
      display={'flex'}
      sx={{
        gap: '8px',
        alignItems: 'center',
        width: 'fit-content',
      }}
    >
      <Box sx={{ width: '120px' }}>
        <LinearProgress
          variant="determinate"
          color={color}
          value={progressValue > 100 ? 100 : progressValue}
          sx={{
            height: 8,
            borderRadius: 5,
            width: '120px',
            [`& .MuiLinearProgress-bar`]: {
              borderRadius: 5,
            },
          }}
        />
      </Box>
      <Typography
        fontSize={14}
        fontWeight={600}
        lineHeight="19px"
        color={color === 'disabled' ? customPalette.disabled.dark : ''}
      >
        {progressValue + '%'}
      </Typography>
    </Box>
  );
};

export const TopTeamProgress = ({ teamId = null }) => {
  const translate = useTranslate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await httpClient(
        apiUrl + '/team-progress' + (teamId ? '/' + teamId : '')
      );
      if (res.status && res.status === 200 && res.json?.data) {
        let data = res.json.data;
        let weight = {
          weight_notes: data.weight_notes,
          weight_rfv: data.weight_rfv,
          weight_subscribers: data.weight_subscribers,
          weight_visits: data.weight_visits,
          team_name: data.name,
        };

        if (teamId && hasRole(['editor'])) {
          data = data.members;
        }

        if (!hasRole(['admin', 'gerente', 'editor'])) {
          data = [data];
        }

        let formattedData = data.map(team => {
          if (teamId && hasRole(['editor'])) {
            team.weight_notes = weight.weight_notes;
            team.weight_rfv = weight.weight_rfv;
            team.weight_subscribers = weight.weight_subscribers;
            team.weight_visits = weight.weight_visits;
            team.team_name = weight.team_name;
          }

          let averageProgress = calculateAverageProgress(team);
          return { ...team, average_progress: averageProgress };
        });

        formattedData.sort((a, b) => b.average_progress - a.average_progress);
        setData(formattedData);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateAverageProgress = team => {
    let visitProgress = 0;
    if (team.target_visits > 0) {
      visitProgress = defaultProgress({
        currentValue: team.visits,
        target: team.target_visits,
      });
      if (visitProgress > 100) visitProgress = 100;
      visitProgress = (visitProgress * team.weight_visits) / 100;
    }

    let notesProgress = 0;
    if (team.target_notes > 0) {
      notesProgress = defaultProgress({
        currentValue: team.notes,
        target: team.target_notes,
      });
      if (notesProgress > 100) notesProgress = 100;
      notesProgress = (notesProgress * team.weight_notes) / 100;
    }

    let rfvProgress = 0;
    if (team.target_rfv > 0) {
      rfvProgress = defaultProgress({
        currentValue: team.rfv,
        target: team.target_rfv,
      });
      if (rfvProgress > 100) rfvProgress = 100;
      rfvProgress = (rfvProgress * team.weight_rfv) / 100;
    }

    let subscribersProgress = 0;
    if (team.target_subscribers > 0) {
      subscribersProgress = defaultProgress({
        currentValue: team.subscribers,
        target: team.target_subscribers,
      });
      if (subscribersProgress > 100) subscribersProgress = 100;
      subscribersProgress =
        (subscribersProgress * team.weight_subscribers) / 100;
    }

    return visitProgress + notesProgress + rfvProgress + subscribersProgress;
  };

  return (
    <Card
      elevation={0}
      sx={{
        background: '#fff',
        color: '#000',
        padding: '24px',
        marginTop: 2,
        marginBottom: '1em',
      }}
    >
      <Typography fontSize={26} fontWeight="600" lineHeight={'31px'}>
        {translate('resources.dashboard.list.top_team_progress.title')}
      </Typography>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
          <Typography variant="h6">{translate('ra.page.loading')}</Typography>
        </Box>
      ) : !loading && data?.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <Typography variant="h6" color={customPalette.disabled.dark}>
            {translate('resources.dashboard.list.top_team_progress.no_data')}
          </Typography>
        </Box>
      ) : (
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {hasRole(['admin', 'gerente', 'editor']) ? (
                    <TableCell sx={{ width: '3%', textAlign: 'center' }}>
                      {translate(
                        'resources.dashboard.list.top_team_progress.fields.top'
                      )}
                    </TableCell>
                  ) : null}

                  <TableCell
                    sx={{
                      width: '15%',
                    }}
                  >
                    {translate(
                      'resources.dashboard.list.top_team_progress.fields.team'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '22%' }}>
                    {translate(
                      'resources.dashboard.list.top_team_progress.fields.members'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    {translate(
                      'resources.dashboard.list.top_team_progress.fields.visits'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    {translate(
                      'resources.dashboard.list.top_team_progress.fields.notes'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    {translate(
                      'resources.dashboard.list.top_team_progress.fields.rfv'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '15%' }}>
                    {translate(
                      'resources.dashboard.list.top_team_progress.fields.subscribers'
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ width: '15%', paddingLeft: '10px !important' }}
                  >
                    {translate(
                      'resources.dashboard.list.top_team_progress.fields.progress'
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((team, i) => {
                  return (
                    <TableRow key={i}>
                      {hasRole(['admin', 'gerente', 'editor']) ? (
                        <TableCell
                          sx={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: customPalette.primary.main,
                            textAlign: 'center',
                          }}
                        >
                          {i + 1}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <Typography
                          sx={{
                            textTransform: 'capitalize',
                            fontSize: 16,
                            fontWeight: 400,
                            lineHeight: '21px',
                          }}
                        >
                          {teamId && hasRole(['editor'])
                            ? team.team_name
                            : team.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: 'flex', marginLeft: '12px' }}>
                        {team.members ? (
                          <AvatarGroup max={6}>
                            {team.members.map((member, index) => (
                              <Avatar
                                key={index}
                                src={member.avatar}
                                alt={member.name}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  marginLeft: '-12px !important',
                                }}
                              />
                            ))}
                          </AvatarGroup>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <Avatar
                              src={team.avatar}
                              alt={team.name}
                              sx={{
                                width: 24,
                                height: 24,
                                marginLeft: '-12px !important',
                              }}
                            />
                            <Typography
                              sx={{
                                textTransform: 'capitalize',
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: '21px',
                              }}
                            >
                              {team.name}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              lineHeight: '7px',
                              display: 'flex',
                              color:
                                team.weight_visits === 0
                                  ? customPalette.disabled.dark
                                  : '',
                            }}
                          >
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.visits)}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 500,
                              lineHeight: '7px',
                              color:
                                team.weight_visits === 0
                                  ? customPalette.disabled.light
                                  : '#888888',
                            }}
                          >
                            &thinsp; /{' '}
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.target_visits)}
                          </Typography>
                        </Box>
                        <ProgressBar
                          currentValue={team.visits}
                          target={team.target_visits}
                          color={
                            team.weight_visits === 0 ? 'disabled' : 'visits'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              lineHeight: '7px',
                              display: 'flex',
                              color:
                                team.weight_notes === 0
                                  ? customPalette.disabled.light
                                  : '',
                            }}
                          >
                            {' '}
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.notes)}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 500,
                              lineHeight: '7px',
                              color:
                                team.weight_notes === 0
                                  ? customPalette.disabled.light
                                  : '#888888',
                            }}
                          >
                            &thinsp; /{' '}
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.target_notes)}
                          </Typography>
                        </Box>
                        <ProgressBar
                          currentValue={team.notes}
                          target={team.target_notes}
                          color={team.weight_notes === 0 ? 'disabled' : 'notes'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              lineHeight: '7px',
                              display: 'flex',
                              color:
                                team.weight_rfv === 0
                                  ? customPalette.disabled.light
                                  : '',
                            }}
                          >
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.rfv)}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 500,
                              lineHeight: '7px',
                              color:
                                team.weight_rfv === 0
                                  ? customPalette.disabled.light
                                  : '#888888',
                            }}
                          >
                            &thinsp; /{' '}
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.target_rfv)}
                          </Typography>
                        </Box>

                        <ProgressBar
                          currentValue={team.rfv}
                          target={team.target_rfv}
                          color={team.weight_rfv === 0 ? 'disabled' : 'rfv'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              lineHeight: '7px',
                              color:
                                team.weight_subscribers === 0
                                  ? customPalette.disabled.light
                                  : '#888888',
                            }}
                          >
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.subscribers)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 500,
                              lineHeight: '7px',
                              color:
                                team.weight_subscribers === 0
                                  ? customPalette.disabled.light
                                  : '#888888',
                            }}
                          >
                            &thinsp; /{' '}
                            {Intl.NumberFormat('es-AR', {
                              maximumFractionDigits: 2,
                            }).format(team.target_subscribers)}
                          </Typography>
                        </Box>

                        <ProgressBar
                          currentValue={team.subscribers}
                          target={team.target_subscribers}
                          color={
                            team.weight_subscribers === 0
                              ? 'disabled'
                              : 'subscribers'
                          }
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          padding: '10px !important',
                          width: '100%',
                          paddingLeft: '2% !important',
                        }}
                      >
                        <ProgressBarGradient
                          percentage={team.average_progress}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      )}
    </Card>
  );
};
